import { type RosterItemResponse } from '@on3/api';
import { useAuth } from '@on3/ui-lib/index';
import { userHasPermission } from '@on3/ui-lib/src/utils/auth/access';
import { rosterEditor } from '@on3/ui-lib/utils/auth/constants';
import { Edit } from '@public/hocs/Roster/Edit/Edit';
import { createContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';

type RosterType = RosterItemResponse[];

type RosterActionType =
  | { type: 'edit'; payload: RosterItemResponse }
  | { type: 'close' };

type RosterActions = {
  edit?: (rosterItem: RosterItemResponse) => void;
};

type RosterActionState = {
  editing: boolean;
  rosterItem: RosterItemResponse | null;
};

const initialState = {
  editing: false,
  rosterItem: null,
} as RosterActionState;

const reducer = (state: RosterActionState, action: RosterActionType) => {
  switch (action.type) {
    case 'edit':
      return { ...state, editing: true, rosterItem: action.payload };
    case 'close':
      return initialState;
    default:
      return state;
  }
};

export const RosterWrapperContext = createContext({
  roster: [] as RosterType,
  rosterItem: null as RosterItemResponse | null,
  close: () => {},
});

export const RosterWrapper = ({
  roster,
  children,
}: {
  roster: RosterType;
  children: (rosterActions: RosterActions) => JSX.Element;
}) => {
  const portal = useRef<HTMLElement | null>(null);
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const canEdit = userHasPermission(rosterEditor, user);

  useEffect(() => {
    if (document) {
      portal.current = document.getElementById('portal-root');
    }
  }, []);

  const close = () => {
    dispatch({ type: 'close' });
  };

  const edit = (rosterItem: RosterItemResponse) => {
    dispatch({ type: 'edit', payload: rosterItem });
  };

  const rosterActions = useMemo(() => {
    const actions = {} as RosterActions;

    if (canEdit) actions.edit = edit;

    return actions;
  }, [canEdit]);

  return (
    <RosterWrapperContext.Provider
      value={{ roster, rosterItem: state.rosterItem, close }}
    >
      {children(rosterActions)}
      {portal.current && (
        <>
          {state.rosterItem &&
            createPortal(
              <Edit rosterItem={state.rosterItem} />,
              portal.current,
            )}
        </>
      )}
    </RosterWrapperContext.Provider>
  );
};
